import { CalendarPlus } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import type { CallbackTime, useCallbackTime } from "@/features/callback-picker";
import { ApiError } from "@/lib/api";
import {
  BACKUP_GAP_MINUTES, CALLBACK_HORIZON_DAYS, MAX_BACKUPS, backupGapMinutes, backupProblem, backupTimesOf, backupTimesText,
  callbackWhen, clockText, defaultBackup, instantOf, isoWithOffset, knownZone, outsideCallingHours, ownZone,
  wallTime, zoneName, type WallTime,
} from "@/lib/callbacks";
import { useRemoveCallbackBackup, useSetCallbackBackup } from "@/lib/queries";
import type { LeadCallback, LeadCard } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * A callback's backup times (ACL-559, ADR-183 and ADR-210 in `platform`): "4 pm, or else 7 pm, then
 * 10 am tomorrow", up to four. Picked under the callback's own time, in the outcome window and on Set
 * outcome, in the time picked on the call, or later on the open callback, on the lead's clock as the
 * callback is.
 *
 * Core-api keeps them on the callback and tells the dialer nothing of them, so the dialer calls the
 * first time exactly as it would have. Only when a call is recorded as missed does core-api book the
 * next, as a callback of its own kept for the rep who last spoke to the lead. So each is said as what
 * happens "if nobody answers", and each is at least 15 minutes after the time before it, the call and
 * its redials end well inside that, or a whole window after it when the cadence works a callback for
 * one (ACL-661, `backupGapMinutes`).
 *
 * Kept apart from `callback-picker.tsx` so a callback with no backup is picked as it always was.
 */

const DAY = 86_400_000;
const HOUR = 3_600_000;

/** The callback's backup times as the picker holds them: none, or times on the callback's own clock,
 *  each with what is wrong with it. */
export type BackupTime = ReturnType<typeof useBackupTime>;

function described(first: CallbackTime, wall: WallTime, before: number | null, later: boolean, gap: number): CallbackTime {
  const instant = instantOf(wall, first.zone);
  const problem = backupProblem(instant, before, Date.now(), later, gap);
  return {
    zone: first.zone,
    leadClock: first.leadClock,
    wall,
    instant,
    problem,
    iso: instant !== null && problem === null ? isoWithOffset(instant, first.zone) : null,
  };
}

/**
 * The backup times' state beside the callback's (`useCallbackTime`): none until `add` opens the first
 * on three hours after the callback, and each `add` after it three hours after the last. They are on
 * the callback's clock, and like the callback keep the same instants when the lead's zone arrives
 * after they were picked. `ok` is false only while one is on and cannot be saved; `isos` is what
 * core-api is sent, empty while there is nothing to send. `opened` starts them at those instants, for
 * a window whose picker is always open.
 */
export function useBackupTime(first: CallbackTime, opened: readonly number[] = [], gapMinutes: number = BACKUP_GAP_MINUTES) {
  const zone = first.zone;
  const [picked, setPicked] = React.useState<{ wall: WallTime; zone: string }[]>(() =>
    opened.slice(0, MAX_BACKUPS).map((at) => ({ wall: wallTime(at, zone), zone })),
  );
  const values: CallbackTime[] = [];
  for (const [i, one] of picked.entries()) {
    const at = one.zone === zone ? null : instantOf(one.wall, one.zone);
    const wall = at === null ? one.wall : wallTime(at, zone);
    const before = i === 0 ? first.instant : values[i - 1]!.instant;
    values.push(described(first, wall, before, i > 0, gapMinutes));
  }
  const firstAt = first.instant;
  const lastAt = values.at(-1)?.instant ?? null;
  const set = React.useCallback(
    (i: number, next: WallTime) => setPicked((all) => all.map((one, j) => (j === i ? { wall: next, zone } : one))),
    [zone],
  );
  const setInstants = React.useCallback(
    (instants: readonly number[]) =>
      setPicked(instants.slice(0, MAX_BACKUPS).map((at) => ({ wall: wallTime(at, zone), zone }))),
    [zone],
  );
  const add = React.useCallback(
    () =>
      setPicked((all) =>
        all.length >= MAX_BACKUPS
          ? all
          : [...all, { wall: wallTime(defaultBackup(lastAt ?? firstAt ?? Date.now()), zone), zone }],
      ),
    [firstAt, lastAt, zone],
  );
  const remove = React.useCallback((i: number) => setPicked((all) => all.filter((_, j) => j !== i)), []);
  const clear = React.useCallback(() => setPicked([]), []);
  const on = values.length > 0;
  const ok = !on || (values.every((v) => v.problem === null) && first.problem === null);
  const isos = on && ok ? values.map((v) => v.iso!) : [];
  return {
    on,
    values,
    set,
    setInstants,
    add,
    remove,
    clear,
    ok,
    isos,
    /** The instants picked, for a time kept until the outcome window (`picked-callback.ts`). */
    instants: values.flatMap((v) => (v.instant === null ? [] : [v.instant])),
    canAdd: values.length < MAX_BACKUPS,
  };
}

/** A callback's own time as a picker's value, for a backup picked against it: one already set. */
export function callbackTimeAt(instant: number, leadZone: string | null | undefined): CallbackTime {
  const lead = knownZone(leadZone);
  const zone = lead ?? ownZone();
  return {
    zone,
    leadClock: lead !== null,
    wall: wallTime(instant, zone),
    instant,
    problem: null,
    iso: isoWithOffset(instant, zone),
  };
}

/** "7:00 PM PDT", with its day when that is not the day of the time before it. */
function backupWords(backup: CallbackTime, before: number | null): string {
  const at = backup.instant!;
  const otherDay = before === null || wallTime(before, backup.zone).date !== backup.wall.date;
  const clock = clockText(at, backup.zone, otherDay);
  return backup.leadClock ? `${clock} ${zoneName(at, backup.zone)}` : `${clock} your time`;
}

/** A time on its next day, on its clock. Counted from that day's midday, so a day the clocks change
 *  on is still one day on. */
function nextDay(time: CallbackTime): number | null {
  const midday = instantOf({ date: time.wall.date, time: "12:00" }, time.zone);
  if (midday === null) return null;
  return instantOf({ date: wallTime(midday + DAY, time.zone).date, time: time.wall.time }, time.zone);
}

const PICK = "rounded-full border px-2 py-0.5 text-2xs transition-colors";
const lit = (on: boolean) =>
  on ? "border-accent-8 bg-accent-2 text-accent-11" : "border-border bg-surface text-text-muted hover:bg-surface-hover";

/**
 * The backup times under the callback's picker: a link to add the first, then each one's day and time
 * on the lead's clock, a few picks counted from the time before it, and what the dialer will do with
 * it, then a link to add another, up to four. `keepOpen` is for a window that is only about the
 * backups: the first is always open, and the window's own footer removes them all. `more` is false
 * where only one may be given (a core-api that keeps one backup).
 */
export function BackupPicker({
  first, backup, disabled, keepOpen = false, more = true,
}: {
  first: CallbackTime;
  backup: BackupTime;
  disabled?: boolean;
  keepOpen?: boolean;
  more?: boolean;
}) {
  if (!backup.on && !keepOpen) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={backup.add}
        className="inline-flex items-center gap-1 self-start text-2xs font-medium text-accent-11 hover:underline disabled:opacity-50"
      >
        <CalendarPlus className="size-3" aria-hidden />
        Add a backup time
      </button>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {backup.values.map((value, i) => (
        <BackupRow
          key={i}
          index={i}
          value={value}
          before={i === 0 ? first : backup.values[i - 1]!}
          backup={backup}
          disabled={disabled}
          removable={!keepOpen || i > 0}
        />
      ))}
      {more && backup.on && backup.canAdd && (
        <button
          type="button"
          disabled={disabled || !backup.ok}
          onClick={backup.add}
          className="inline-flex items-center gap-1 self-start text-2xs font-medium text-accent-11 hover:underline disabled:opacity-50"
        >
          <CalendarPlus className="size-3" aria-hidden />
          Add another backup time ({backup.values.length} of {MAX_BACKUPS})
        </button>
      )}
    </div>
  );
}

/** One backup time: its day and time, the picks counted from the time before it, and what happens. */
function BackupRow({
  index, value, before, backup, disabled, removable,
}: {
  index: number;
  value: CallbackTime;
  /** The time before it: the callback's own for the first backup, else the backup before it. */
  before: CallbackTime;
  backup: BackupTime;
  disabled?: boolean;
  removable: boolean;
}) {
  const id = React.useId();
  const now = Date.now();
  const today = wallTime(now, value.zone).date;
  const last = wallTime(now + CALLBACK_HORIZON_DAYS * DAY, value.zone).date;
  // "Backup date" for the first, as it always was; "Backup 2 date" for the next.
  const name = index === 0 ? "Backup" : `Backup ${index + 1}`;
  // The picks count from the time before: "an hour later", "the same time the next day".
  const picks =
    before.instant === null
      ? []
      : [
          { label: "+1 hour", instant: before.instant + HOUR },
          { label: "+3 hours", instant: before.instant + 3 * HOUR },
          { label: "Same time tomorrow", instant: nextDay(before) },
        ];
  const late = value.leadClock && value.instant !== null && value.problem === null && outsideCallingHours(value.instant, value.zone);

  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5" disabled={disabled}>
      <legend className="mb-1.5 text-xs text-text-muted">
        <span className="font-medium text-text">
          {index === 0 ? "Or else, if nobody answers" : "Then, if nobody answers again"}
        </span>{" "}
        {value.leadClock ? `· their time (${zoneName(value.instant ?? now, value.zone)})` : "· your time: no time zone is known for them"}
      </legend>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,7.5rem)] gap-1.5">
        <Input
          id={`${id}-date`}
          type="date"
          aria-label={`${name} date`}
          min={today}
          max={last}
          required
          value={value.wall.date}
          invalid={value.problem !== null && value.wall.date !== ""}
          onChange={(e) => backup.set(index, { ...value.wall, date: e.target.value })}
        />
        <Input
          id={`${id}-time`}
          type="time"
          aria-label={`${name} time`}
          step={60}
          required
          value={value.wall.time}
          invalid={value.problem !== null && value.wall.time !== ""}
          onChange={(e) => backup.set(index, { ...value.wall, time: e.target.value })}
        />
      </div>
      {picks.length > 0 && (
        <div className="flex flex-wrap gap-1" role="group" aria-label={`${name} picks`}>
          {picks.map((pick) => {
            if (pick.instant === null) return null;
            const wall = wallTime(pick.instant, value.zone);
            const on = wall.date === value.wall.date && wall.time === value.wall.time;
            return (
              <button
                key={pick.label}
                type="button"
                aria-pressed={on}
                onClick={() => backup.set(index, wall)}
                className={cn(PICK, lit(on))}
              >
                {pick.label}
              </button>
            );
          })}
        </div>
      )}
      {value.problem ? (
        <p role="alert" className="text-2xs text-danger-11">
          {value.problem}
        </p>
      ) : (
        value.instant !== null &&
        before.instant !== null && (
          <p className="text-2xs text-text-muted" aria-live="polite">
            {index === 0
              ? `If the dialer's call at ${clockText(before.instant, before.zone, false)} reaches nobody, it calls again at `
              : "If that call reaches nobody too, it calls again at "}
            {backupWords(value, before.instant)}.
          </p>
        )
      )}
      {late && (
        <p className="text-2xs text-warning-11">
          Outside 8 AM–9 PM for them: the dialer waits until their calling hours open.
        </p>
      )}
      {removable && (
        <button
          type="button"
          onClick={() => backup.remove(index)}
          className="self-start text-2xs font-medium text-danger-11 hover:underline"
        >
          {index === 0 ? "Remove backup" : "Remove this time"}
        </button>
      )}
    </fieldset>
  );
}

/**
 * The lead already has a callback still to come, picked for a time: a new callback outcome replaces
 * it (one open callback per lead, ADR-080). Said before it is saved, with the two ways to keep the
 * old time as one of the two: as the backup of the new one, or first with the new one as its backup.
 * Both only fill the pickers; nothing goes to core-api until the outcome is saved.
 */
export function OpenCallbackChoice({
  open, callback, backup, gapMinutes = BACKUP_GAP_MINUTES,
}: {
  open: LeadCallback | null | undefined;
  callback: ReturnType<typeof useCallbackTime>;
  backup: BackupTime;
  /** The gap a backup keeps from the time before it (`backupGapMinutes`). */
  gapMinutes?: number;
}) {
  if (!open || open.status !== "upcoming" || !open.picked) return null;
  const { value } = callback;
  const due = Date.parse(open.due_at);
  const lead = value.leadClock ? value.zone : null;
  const there = callbackWhen(due, lead).there;
  const kept = backupTimesOf(open);
  const old = clockText(due, value.zone, true);
  const gap = gapMinutes * 60_000;
  const picked = value.instant !== null && value.problem === null ? value.instant : null;
  const asBackup = picked !== null && due >= picked + gap;
  const asFirst = picked !== null && picked >= due + gap;
  return (
    <Callout tone="warning">
      <p>
        They have a callback at {there}
        {kept.length === 1 && ` and its backup at ${callbackWhen(kept[0]!, lead).there}`}
        {kept.length > 1 && ` and backups at ${backupTimesText(kept, value.zone, value.leadClock, due)}`}. Scheduling this one
        replaces it.
      </p>
      {(asBackup || asFirst) && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {asBackup && (
            <button
              type="button"
              className="font-medium underline-offset-2 hover:underline"
              onClick={() => backup.setInstants([due])}
            >
              Use {old} as the backup
            </button>
          )}
          {asFirst && (
            <button
              type="button"
              className="font-medium underline-offset-2 hover:underline"
              onClick={() => {
                callback.set(wallTime(due, value.zone));
                backup.setInstants([picked]);
              }}
            >
              Keep {old} first, make this the backup
            </button>
          )}
        </div>
      )}
    </Callout>
  );
}

/**
 * The open callback's backup times, added or changed after it was set (`PUT …/backup`), or taken away
 * (`DELETE`). The callback's own time stays as it is, and nothing reaches the dialer. More than one
 * only from a core-api that keeps several (`backup_times` on the callback, ADR-210 in `platform`).
 */
export function CallbackBackupDialog({
  contact, callback, onClose,
}: {
  contact: LeadCard;
  callback: LeadCallback;
  onClose: () => void;
}) {
  const toast = useToast();
  const leadZone = contact.clock_timezone ?? contact.timezone;
  const due = Date.parse(callback.due_at);
  const first = React.useMemo(() => callbackTimeAt(due, leadZone), [due, leadZone]);
  const kept = backupTimesOf(callback);
  const backup = useBackupTime(
    first,
    kept.length > 0 ? kept.map((at) => Date.parse(at)) : [defaultBackup(due)],
    backupGapMinutes(contact.callback_window_minutes),
  );
  const several = "backup_times" in callback;
  const save = useSetCallbackBackup();
  const remove = useRemoveCallbackBackup();
  const [error, setError] = React.useState<string | null>(null);
  const lead = first.leadClock ? first.zone : null;
  const there = callbackWhen(due, lead).there;
  // "The Fri 4:00 PM callback is unchanged."
  const unchanged = `The ${clockText(due, first.zone, true)} callback is unchanged.`;
  const failed = (e: unknown) => setError(e instanceof ApiError ? e.message : "Could not save the backup times.");
  const busy = save.isPending || remove.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {kept.length > 1 ? "Change the backup times" : kept.length === 1 ? "Change the backup time" : "Backup time"}
          </DialogTitle>
          <DialogDescription>
            Kept with the callback at {there}, which stays as it is. If the dialer's call then reaches nobody, it
            calls again at the first backup time, and at the next each time nobody answers.
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          aria-label="The backup times"
          onSubmit={(e) => {
            e.preventDefault();
            if (backup.isos.length === 0) return;
            setError(null);
            save.mutate(
              { leadId: contact.id, callbackId: callback.id, backupTimes: backup.isos },
              {
                onSuccess: (saved) => {
                  const times = backupTimesOf(saved);
                  const said =
                    times.length > 1
                      ? backupTimesText(times, first.zone, first.leadClock, due)
                      : callbackWhen(times[0] ?? backup.isos[0]!, lead).there;
                  toast.success(`Backup saved · ${said}`, unchanged);
                  onClose();
                },
                onError: failed,
              },
            );
          }}
        >
          <DialogBody className="flex flex-col gap-3">
            <BackupPicker first={first} backup={backup} disabled={busy} keepOpen more={several} />
            {error && <Callout tone="danger">{error}</Callout>}
          </DialogBody>
          <DialogFooter>
            {callback.backup_at && (
              <Button
                type="button"
                variant="ghost"
                className="mr-auto text-danger-11"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  remove.mutate(
                    { leadId: contact.id, callbackId: callback.id },
                    {
                      onSuccess: () => {
                        toast.success("Backup removed", unchanged);
                        onClose();
                      },
                      onError: (e) => setError(e instanceof ApiError ? e.message : "Could not remove the backup time."),
                    },
                  );
                }}
              >
                {kept.length > 1 ? "Remove all backups" : "Remove backup"}
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={save.isPending} disabled={backup.isos.length === 0 || busy}>
              Save backup
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
